import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { installInputModality } from './input-modality.js';
import { createTooltipGroup, DEFAULT_TOOLTIP_TIMING } from './tooltip-group.js';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE REACT SIDE OF THE TOOLTIP GROUP
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `BookoraThemeProvider` mounts one `TooltipGroupProvider` for the whole panel.
 * A tree rendered without it (a unit test of a single component) falls back to
 * one module-level group, so a Tooltip never needs a provider to work and two
 * sibling tests never share a half-open bubble through React state.
 *
 * The DOM listeners (scroll, blur, visibility, outside pointer, pinned Esc,
 * input modality) are attached by the tooltips themselves and ref-counted per
 * group, so they exist exactly while at least one kit tooltip is mounted.
 */
let fallbackGroup = null;
function getFallbackGroup() {
    fallbackGroup ??= createTooltipGroup(DEFAULT_TOOLTIP_TIMING);
    return fallbackGroup;
}
const TooltipGroupContext = createContext(null);
/** One delay group for every kit tooltip below it. */
export function TooltipGroupProvider({ children, timing }) {
    // The timing is read once on purpose: a new group mid-session would orphan
    // whatever bubble the old one had open.
    const [group] = useState(() => createTooltipGroup(timing ?? DEFAULT_TOOLTIP_TIMING));
    return _jsx(TooltipGroupContext.Provider, { value: group, children: children });
}
/** The nearest group, or the module-level fallback. */
export function useTooltipGroup() {
    return useContext(TooltipGroupContext) ?? getFallbackGroup();
}
const attachments = new WeakMap();
/**
 * Keep the group's document listeners and the input-modality listeners
 * installed while the calling tooltip is mounted. Ref-counted.
 */
export function useTooltipGroupAttachment(group) {
    useEffect(() => {
        if (typeof document === 'undefined')
            return undefined;
        const detachModality = installInputModality(document);
        const entry = attachments.get(group);
        if (entry)
            entry.count += 1;
        else
            attachments.set(group, { count: 1, detach: group.attach(document) });
        return () => {
            detachModality();
            const current = attachments.get(group);
            if (!current)
                return;
            current.count -= 1;
            if (current.count <= 0) {
                current.detach();
                attachments.delete(group);
            }
        };
    }, [group]);
}
/**
 * While `active`, closes any open tooltip and blocks new ones.
 *
 * Only for MODAL-ISH surfaces: a spotlight, a coach-mark step, a blocking
 * overlay. A passive, always-visible surface (a suggestion card, a banner)
 * must not call it, or tooltips vanish app-wide whenever the card is on
 * screen.
 */
export function useTooltipSuppression(active) {
    const group = useTooltipGroup();
    useEffect(() => {
        if (!active)
            return undefined;
        return group.suspend();
    }, [active, group]);
}
/**
 * Provided by the kit's `Dropdown`, `Popover` and `Popconfirm` around their
 * TRIGGER, and reset to `null` inside their popup — so a tooltip inside the
 * open overlay is unaffected.
 *
 * - The kit `Tooltip` reads it: while the owning overlay is open it refuses to
 *   open, and closes if it was open. The popup has taken the space.
 * - Kit `Button`/`IconButton` read it for `aria-haspopup` + `aria-expanded`,
 *   only for click overlays and only where the caller did not set them.
 */
export const PopupTriggerContext = createContext(null);
export function PopupTriggerProvider({ value, children, }) {
    return _jsx(PopupTriggerContext.Provider, { value: value, children: children });
}
/**
 * `aria-haspopup`/`aria-expanded` for a kit button that triggers a click
 * overlay. Each is filled independently, only where the caller left it
 * unset: a caller that states `aria-haspopup="menu"` (the «راهنما» trigger)
 * still gets the live `aria-expanded` — rc-dropdown adds none of its own, so
 * skipping both left the menu's state unannounced.
 */
export function usePopupTriggerAria(props) {
    const ctx = useContext(PopupTriggerContext);
    if (!ctx || !ctx.clickTrigger)
        return null;
    const aria = {};
    if (props['aria-haspopup'] === undefined)
        aria['aria-haspopup'] = ctx.haspopup;
    if (props['aria-expanded'] === undefined)
        aria['aria-expanded'] = ctx.open;
    return aria;
}
//# sourceMappingURL=tooltip-context.js.map